import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { catalogService } from "../../utils/services/catalogService";
import { catalogProductService } from "../../utils/services/catalogProductService";
import { formatDate } from "../../utils/formatDate";

function formatVariantsText(variants) {
  if (!variants || variants.length === 0) return "—";
  const skus = variants.map((v) => v.sku).filter(Boolean);

  function getCommonPrefix(skuList) {
    if (!skuList || skuList.length <= 1) return "";
    let prefix = skuList[0];
    for (let i = 1; i < skuList.length; i++) {
      while (!skuList[i].startsWith(prefix)) {
        prefix = prefix.slice(0, -1);
        if (!prefix) return "";
      }
    }
    return prefix.length >= 3 ? prefix : "";
  }

  const prefix = getCommonPrefix(skus);
  if (prefix) {
    const parts = variants.map((v) => {
      const suffix = v.sku.slice(prefix.length);
      const price =
        v.price !== "" && v.price != null
          ? `$${Number(v.price).toFixed(2)}`
          : "—";
      return `${suffix || v.sku}-${price}`;
    });
    return `${prefix}(${parts.join(", ")})`;
  }
  return variants
    .map((v) => {
      const price =
        v.price !== "" && v.price != null
          ? `$${Number(v.price).toFixed(2)}`
          : "—";
      return `${v.sku}-${price}`;
    })
    .join(", ");
}

const DetailCard = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      backgroundColor: "var(--bg-secondary)",
      border: "1px solid var(--border-color)",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "var(--primary-light)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      },
    }}
  >
    <Typography
      fontSize="11px"
      fontFamily="Poppins"
      color="var(--text-tertiary)"
      fontWeight={700}
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        mb: 0.75,
        opacity: 0.8,
      }}
    >
      {label}
    </Typography>
    <Typography
      fontSize="14px"
      fontFamily="Poppins"
      color="var(--text-primary)"
      fontWeight={500}
      sx={{ wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Paper>
);

const CatalogViewModal = React.memo(({ open, onClose, catalog, client_id }) => {
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && catalog?._id) {
      setLoading(true);
      Promise.all([
        catalogService.fetchById(catalog._id),
        catalogProductService.fetchByCatalog(catalog._id),
      ])
        .then(([data, cpResult]) => {
          const products = cpResult?.data || cpResult || [];
          setSelectedCatalog({ ...data, products });
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch catalog:", err);
          setLoading(false);
        });
    }
  }, [catalog?._id, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--bg-paper)",
          borderBottom: "2px solid var(--border-color)",
          py: 2,
          px: 3,
        }}
      >
        <Box>
          <Typography
            fontFamily="Poppins"
            fontWeight={700}
            fontSize="18px"
            color="var(--text-primary)"
          >
            Catalog Details
          </Typography>
          {selectedCatalog && (
            <Typography
              fontSize="12px"
              color="var(--text-tertiary)"
              sx={{ mt: 0.5 }}
            >
              ID: {selectedCatalog.uid}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{
            color: "var(--text-tertiary)",
            "&:hover": { backgroundColor: "var(--border-color)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "var(--bg-paper)", pt: 3, pb: 2 }}>
        {loading ? (
          <Box>
            {/* Details Skeleton */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton height={40} sx={{ mb: 1 }} />
                  <Skeleton height={20} width="80%" />
                </Grid>
              ))}
            </Grid>

            {/* Products Section Skeleton */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Skeleton height={28} width={240} />
            </Box>
            <Box
              sx={{
                border: "1px solid var(--border-color)",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{ p: 1.5, borderBottom: "1px solid var(--border-color)" }}
                >
                  <Skeleton height={32} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : selectedCatalog ? (
          <Box>
            {/* Main Details Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={client_id ? 4 : 3}>
                <DetailCard label="UID" value={selectedCatalog.uid} />
              </Grid>
              <Grid item xs={12} sm={6} md={client_id ? 4 : 3}>
                <DetailCard label="Name" value={selectedCatalog.name} />
              </Grid>
              <Grid item xs={12} sm={6} md={client_id ? 4 : 3}>
                <DetailCard
                  label="Type"
                  value={selectedCatalog.type?.name || selectedCatalog.type}
                />
              </Grid>
              {client_id ? (
                <></>
              ) : (
                <Grid item xs={12} sm={6} md={3}>
                  <DetailCard
                    label="Client"
                    value={
                      selectedCatalog.client_id?.business_name ||
                      selectedCatalog.client_id?.name ||
                      selectedCatalog.client_id
                    }
                  />
                </Grid>
              )}
            </Grid>

            {/* Dates Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <DetailCard
                  label="Valid From"
                  value={formatDate(selectedCatalog.valid_from, "—")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailCard
                  label="Valid Till"
                  value={formatDate(selectedCatalog.valid_till, "—")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailCard
                  label="Print Date"
                  value={formatDate(selectedCatalog.print_date, "—")}
                />
              </Grid>
            </Grid>

            {/* Products Section */}
            {selectedCatalog.products &&
              selectedCatalog.products.length > 0 && (
                <Box>
                  <Divider
                    sx={{
                      mb: 2.5,
                      borderColor: "var(--border-color)",
                      opacity: 0.5,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={700}
                      fontSize="16px"
                      color="var(--text-primary)"
                    >
                      Products
                    </Typography>
                    <Chip
                      label={selectedCatalog.products.length}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 600,
                        borderColor: "var(--border-color)",
                      }}
                    />
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: "1px solid var(--border-color)",
                      borderRadius: "8px",
                      overflow: "auto",
                      maxHeight: 450,
                      backgroundColor: "var(--bg-secondary)",
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {[
                            ...(selectedCatalog.type?.name !== "Online"
                              ? ["Page"]
                              : []),
                            "Product",
                            "SKU / Price",
                          ].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                backgroundColor: "var(--table-header-bg)",
                                color: "var(--text-tertiary)",
                                fontSize: "12px",
                                fontWeight: 700,
                                fontFamily: "Poppins",
                                py: 1,
                                px: 1.5,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                width:
                                  h === "Page"
                                    ? 80
                                    : h === "Product"
                                      ? "35%"
                                      : undefined,
                                borderBottom: "2px solid var(--border-color)",
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCatalog.products
                          .sort((a, b) => a.page - b.page)
                          .map((p, i) => (
                            <TableRow
                              key={i}
                              sx={{
                                height: "45px",
                                transition: "background-color 0.15s ease",
                                "&:nth-of-type(odd)": {
                                  backgroundColor: "var(--row-bg-odd)",
                                },
                                "&:nth-of-type(even)": {
                                  backgroundColor: "var(--row-bg-even)",
                                },
                                "&:hover": {
                                  backgroundColor: "var(--table-hover-bg)",
                                },
                              }}
                            >
                              {selectedCatalog.type?.name !== "Online" && (
                                <TableCell
                                  key={i}
                                  sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderColor: "var(--border-color)",
                                  }}
                                >
                                  <Chip
                                    label={p.page ?? "—"}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontFamily: "Poppins",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      borderColor: "var(--border-color)",
                                    }}
                                  />
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  px: 1.5,
                                  py: 0.75,
                                  borderColor: "var(--border-color)",
                                }}
                              >
                                <Typography
                                  fontSize="13px"
                                  fontFamily="Poppins"
                                  color="var(--text-primary)"
                                  fontWeight={500}
                                >
                                  {p.product?.name || `Product ${i + 1}`}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 1.5,
                                  py: 0.75,
                                  borderColor: "var(--border-color)",
                                }}
                              >
                                <Typography
                                  fontSize="12px"
                                  fontFamily="Poppins"
                                  color="var(--text-secondary)"
                                  sx={{
                                    wordBreak: "break-word",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {formatVariantsText(p.variants)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: "var(--bg-paper)",
          borderTop: "2px solid var(--border-color)",
          py: 1.5,
          px: 3,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            fontWeight: 600,
            borderColor: "var(--border-color)",
            color: "var(--text-primary)",
            "&:hover": {
              backgroundColor: "var(--border-color)",
              borderColor: "var(--border-color)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

CatalogViewModal.displayName = "CatalogViewModal";

export default CatalogViewModal;
